import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type { WalkerSessionDto } from "../types/auth";
import type { WalkerConversationListItemDto } from "../types/conversations";

type LoadState = "loading" | "ready" | "error";

type StripeStatusDto = {
  stripeConnected?: boolean;
  stripeAccountId?: string | null;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  onboardingComplete?: boolean;
  requiresOnboarding?: boolean;
};

const WalkerDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>("loading");
  const [session, setSession] = useState<WalkerSessionDto | null>(null);
  const [inbox, setInbox] = useState<WalkerConversationListItemDto[]>([]);
  const [stripeStatus, setStripeStatus] = useState<StripeStatusDto | null>(null);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [me, inboxItems] = await Promise.all([
          walkiesClient.getWalkerMe(),
          walkiesClient.getWalkerInbox(),
        ]);

        setSession(me);
        setInbox(inboxItems);

        try {
          const status = await walkiesClient.getWalkerStripeStatus();
          setStripeStatus(status);
        } catch {
          setStripeStatus(null);
        }

        setState("ready");
      } catch {
        setError("You are not signed in.");
        setState("error");
        navigate("/walker/access", { replace: true });
      }
    }

    void load();
  }, [navigate]);

  const unreadCount = useMemo(
    () => inbox.reduce((sum, item) => sum + (item.unreadCount ?? 0), 0),
    [inbox]
  );

  const stripeReady = !!(
    stripeStatus?.stripeConnected &&
    stripeStatus?.chargesEnabled &&
    stripeStatus?.payoutsEnabled &&
    stripeStatus?.detailsSubmitted
  );

  async function handleConnectStripe() {
    try {
      setStripeBusy(true);
      setStripeError("");

      const result = await walkiesClient.createWalkerStripeConnectLink();

      if (!result.url?.trim()) {
        throw new Error("Stripe onboarding link was not returned.");
      }

      window.location.href = result.url;
    } catch (err) {
      setStripeError(
        err instanceof Error
          ? err.message
          : "Unable to start Stripe onboarding."
      );
    } finally {
      setStripeBusy(false);
    }
  }

  if (state === "loading") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Loading dashboard…</h1>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Unable to load dashboard</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.badgeRow}>
            <div style={styles.badge}>Walker dashboard</div>

            {unreadCount > 0 && (
              <div style={styles.unreadBadge}>
                {unreadCount} new {unreadCount === 1 ? "enquiry" : "enquiries"}
              </div>
            )}
          </div>

          <h1 style={styles.title}>Welcome {session?.firstName}</h1>

          <p style={styles.text}>
            Profile: <strong>{session?.displayTitle}</strong>
          </p>

          <p style={styles.text}>Email: {session?.email}</p>

          <p style={styles.textMuted}>
            {unreadCount > 0
              ? `You have ${unreadCount} unread ${
                  unreadCount === 1 ? "message" : "messages"
                } from dog owners.`
              : "You have no unread owner messages right now."}
          </p>

          <div style={styles.stripePanel}>
            <div style={styles.stripePanelHeader}>
              <div>
                <h2 style={styles.stripeTitle}>Stripe payouts</h2>
                <p style={styles.stripeText}>
                  Connect Stripe so owners can pay your booking proposals securely.
                </p>
              </div>

              <div
                style={{
                  ...styles.stripeStatusBadge,
                  ...(stripeReady
                    ? styles.stripeStatusReady
                    : styles.stripeStatusPending),
                }}
              >
                {stripeReady ? "Connected" : "Setup required"}
              </div>
            </div>

            {stripeStatus?.stripeAccountId && (
              <div style={styles.stripeMeta}>
                Stripe account: {stripeStatus.stripeAccountId}
              </div>
            )}

            {!stripeReady && (
              <div style={styles.stripeChecklist}>
                <div>
                  Details submitted: {stripeStatus?.detailsSubmitted ? "Yes" : "No"}
                </div>
                <div>
                  Charges enabled: {stripeStatus?.chargesEnabled ? "Yes" : "No"}
                </div>
                <div>
                  Payouts enabled: {stripeStatus?.payoutsEnabled ? "Yes" : "No"}
                </div>
              </div>
            )}

            {stripeError && <div style={styles.errorBox}>{stripeError}</div>}

            <div style={styles.actions}>
              <button
                style={styles.primaryButton}
                onClick={() => navigate("/walker/messages")}
              >
                {unreadCount > 0
                  ? `View messages (${unreadCount} new)`
                  : "View messages"}
              </button>

              <button
                style={styles.secondaryButton}
                onClick={() => navigate("/walker/bookings")}
              >
                View bookings
              </button>

              <button
                style={styles.secondaryButton}
                onClick={handleConnectStripe}
                disabled={stripeBusy}
              >
                {stripeBusy
                  ? "Opening Stripe..."
                  : stripeReady
                  ? "Refresh Stripe setup"
                  : "Connect Stripe"}
              </button>

              <button
                style={styles.secondaryButton}
                onClick={async () => {
                  await walkiesClient.signOutWalker();
                  navigate("/");
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242,138,22,0.16), transparent 28%), linear-gradient(180deg,#2f4f46 0%,#213a33 100%)",
    padding: "32px 16px 64px",
    color: "#fff",
  },

  container: {
    maxWidth: 760,
    margin: "0 auto",
  },

  card: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 28,
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
  },

  badgeRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 16,
  },

  badge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(47,79,70,0.08)",
    fontWeight: 800,
  },

  unreadBadge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(242, 138, 22, 0.12)",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#b45309",
    fontWeight: 800,
  },

  title: {
    margin: "0 0 12px",
    fontSize: 38,
    fontWeight: 900,
    color: "#213a33",
  },

  text: {
    margin: "6px 0",
    fontSize: 16,
  },

  textMuted: {
    margin: "14px 0 0",
    fontSize: 15,
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.6,
  },

  stripePanel: {
    marginTop: 24,
    padding: 20,
    borderRadius: 22,
    background: "rgba(33, 58, 51, 0.04)",
    border: "1px solid rgba(33, 58, 51, 0.08)",
  },

  stripePanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },

  stripeTitle: {
    margin: "0 0 8px",
    fontSize: 24,
    fontWeight: 900,
    color: "#213a33",
  },

  stripeText: {
    margin: 0,
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.6,
  },

  stripeMeta: {
    marginTop: 12,
    fontSize: 14,
    color: "rgba(24, 32, 29, 0.7)",
  },

  stripeChecklist: {
    marginTop: 14,
    display: "grid",
    gap: 6,
    fontWeight: 700,
    color: "#213a33",
  },

  stripeStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 13,
  },

  stripeStatusReady: {
    background: "rgba(21, 128, 61, 0.12)",
    border: "1px solid rgba(21, 128, 61, 0.22)",
    color: "#166534",
  },

  stripeStatusPending: {
    background: "rgba(242, 138, 22, 0.12)",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#b45309",
  },

  errorBox: {
    marginTop: 14,
    borderRadius: 16,
    padding: 14,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.14)",
    color: "#7f1d1d",
    fontWeight: 700,
  },

  actions: {
    marginTop: 24,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },

  primaryButton: {
    padding: "12px 18px",
    borderRadius: 16,
    border: "none",
    background: "#f28a16",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },

  secondaryButton: {
    padding: "12px 18px",
    borderRadius: 16,
    border: "none",
    background: "#213a33",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
};

export default WalkerDashboardPage;